'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiCardContent, {
  type CardContentTypeMap as MuiCardContentTypeMap,
} from '@mui/material/CardContent';
import { type SxProps, type Theme } from '@mui/material/styles';

type ExcludedMuiProps = '';

type CustomProps = {
  component?: ElementType;
};

type CardContentTypeMap<
  P = CustomProps,
  D extends ElementType = MuiCardContentTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiCardContentTypeMap['props'], ExcludedMuiProps> & P;
};

export type CardContentProps<
  D extends ElementType = MuiCardContentTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiCardContentTypeMap<P, D>, D>;

const CardContentComponent = (
  { sx = [], ...props }: CardContentProps,
  ref: Ref<HTMLDivElement>
) => {
  const resolvedSx: SxProps<Theme> = {
    '&:last-child': {
      p: (theme) => theme.spacing(2),
    },
  };
  return (
    <MuiCardContent
      {...props}
      ref={ref}
      sx={[resolvedSx, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
};

CardContentComponent.displayName = 'CardContent';

export const CardContent = forwardRef(
  CardContentComponent
) as OverridableComponent<CardContentTypeMap>;
